import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { ProductSchedule, ScheduleStore } from '@nucleus-commerce/api-client/admin';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@nucleus-commerce/ui/components/alert-dialog';

import { useRuleFields } from '@/features/product-rules';

import { fromSchedule, toUpdateSchedule } from '../api/mappers';
import { useUpdateSchedule } from '../hooks/use-schedule-mutations';
import { useStoreOptions } from '../hooks/use-store-options';

import { ScheduleForm } from './schedule-form';

export interface SettingsTabProps {
  schedule: ProductSchedule;
  canWrite: boolean;
  /** A removed store with live products ran a release run: open it. */
  onReleased: (runId: string) => void;
}

/** Target stores the values drop that still have products live through this schedule. */
function removedLiveStores(
  schedule: ProductSchedule,
  storeIds: ReadonlySet<string>,
): ScheduleStore[] {
  return schedule.stores.filter(
    (store) => !storeIds.has(store.storeId) && store.placements.live > 0,
  );
}

/** The schedule's settings on the save bar; an ended schedule shows them read-only. */
export function SettingsTab({ schedule, canWrite, onReleased }: SettingsTabProps) {
  const { t } = useTranslation(['schedules', 'common']);
  const update = useUpdateSchedule(schedule.id);
  const stores = useStoreOptions(schedule.stores);
  const { kindOf, isLoading } = useRuleFields();
  const ended = schedule.status === 'ended';
  const [confirming, setConfirming] = useState<ScheduleStore[] | null>(null);
  const answer = useRef<((ok: boolean) => void) | null>(null);
  // Rule values are typed by the dictionary, so wait for it before seeding the form. A refetch
  // (every run, pause, resume, or the 5 s poll while a run is going) hands in new defaults, which
  // the form re-seeds in place without dropping unsaved edits.
  const defaults = useMemo(
    () => (isLoading ? null : fromSchedule(schedule, kindOf)),
    [isLoading, kindOf, schedule],
  );
  if (!defaults) return null;

  /** Asks before a save that unpublishes what is live in removed stores; true to go ahead. */
  const confirmRemoval = (removed: ScheduleStore[]) =>
    new Promise<boolean>((resolve) => {
      answer.current = resolve;
      setConfirming(removed);
    });
  const settle = (ok: boolean) => {
    answer.current?.(ok);
    answer.current = null;
    setConfirming(null);
  };

  return (
    <div className="flex flex-col gap-4">
      {ended ? (
        <Alert role="status">
          <AlertDescription>{t('schedules:settings.ended')}</AlertDescription>
        </Alert>
      ) : schedule.status === 'active' ? (
        <p className="text-muted-foreground text-sm">{t('schedules:settings.activeHint')}</p>
      ) : null}
      <ScheduleForm
        key={schedule.id}
        mode="edit"
        defaultValues={defaults}
        stores={stores}
        isSubmitting={update.isPending}
        disabled={!canWrite || ended}
        onSubmit={async (values, resolveKind) => {
          const body = toUpdateSchedule(values, defaults, resolveKind);
          if (Object.keys(body).length === 0) return true;
          if (body.stores) {
            const removed = removedLiveStores(
              schedule,
              new Set(values.stores.map((s) => s.storeId)),
            );
            if (removed.length > 0 && !(await confirmRemoval(removed))) return false;
          }
          const change = await update.mutateAsync(body);
          if (change.releaseRunId) onReleased(change.releaseRunId);
          return true;
        }}
      />
      <AlertDialog
        open={confirming !== null}
        onOpenChange={(open) => {
          if (!open) settle(false);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('schedules:settings.removeStores.title', { count: confirming?.length ?? 0 })}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t('schedules:settings.removeStores.description', {
                stores: (confirming ?? []).map((s) => s.storeName).join(', '),
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common:actions.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive hover:bg-destructive/90 text-white"
              onClick={() => settle(true)}
            >
              {t('schedules:settings.removeStores.confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
